/**
 * Curated Supplier Store — Firestore I/O for the platform-wide supplier
 * directory overlay and per-company supplier additions.
 *
 * Schema:
 *   curated_suppliers/{supplierId} → Supplier + metadata
 *   companies/{companyId}/suppliers/{supplierId} → Supplier + metadata
 */
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import type { Supplier } from '../../types/supplyChain';

export const CURATED_SUPPLIERS_COLLECTION = 'curated_suppliers';

export type CuratedSupplierSource = 'baseline-seed' | 'admin-manual';

export interface CuratedSupplierDoc extends Supplier {
  updatedAt?: unknown;
  updatedByEmail?: string;
  source?: CuratedSupplierSource;
  /** When false, hidden from directory browse (soft delete). */
  active?: boolean;
}

export interface CompanySupplierDoc extends Supplier {
  updatedAt?: unknown;
  updatedByEmail?: string;
  active?: boolean;
}

function companySuppliersCol(companyId: string) {
  return collection(db, `companies/${companyId}/suppliers`);
}

function parseSupplierDoc(data: Record<string, unknown>, id: string): Supplier | null {
  if (typeof data.name !== 'string' || !Array.isArray(data.categories)) return null;
  const hq = data.hq as { country?: string; city?: string } | undefined;
  if (!hq || typeof hq.country !== 'string') return null;
  if (data.active === false) return null;
  return {
    id,
    name: data.name,
    categories: data.categories as Supplier['categories'],
    hq: { country: hq.country, ...(hq.city ? { city: hq.city } : {}) },
    ...(Array.isArray(data.certifications) ? { certifications: data.certifications as Supplier['certifications'] } : {}),
    ...(data.typicalLeadMonths ? { typicalLeadMonths: data.typicalLeadMonths as Supplier['typicalLeadMonths'] } : {}),
    ...(data.reliabilityTier ? { reliabilityTier: data.reliabilityTier as Supplier['reliabilityTier'] } : {}),
    ...(data.financialHealthTier ? { financialHealthTier: data.financialHealthTier as Supplier['financialHealthTier'] } : {}),
    ...(typeof data.website === 'string' ? { website: data.website } : {}),
    ...(typeof data.logoUrl === 'string' ? { logoUrl: data.logoUrl } : {}),
    ...(typeof data.capacity === 'string' ? { capacity: data.capacity } : {}),
    ...(data.typicalPricingIndex ? { typicalPricingIndex: data.typicalPricingIndex as Supplier['typicalPricingIndex'] } : {}),
    ...(typeof data.notes === 'string' ? { notes: data.notes } : {}),
    ...(Array.isArray(data.contacts) ? { contacts: data.contacts as Supplier['contacts'] } : {}),
    ...(data.partnership ? { partnership: data.partnership as Supplier['partnership'] } : {}),
    ...(Array.isArray(data.catalog) ? { catalog: data.catalog as Supplier['catalog'] } : {}),
    ...(data.dataFreshness ? { dataFreshness: data.dataFreshness as Supplier['dataFreshness'] } : {}),
  };
}

/** Load platform curated supplier overlays. Returns [] on failure. */
export async function fetchCuratedSuppliers(): Promise<Supplier[]> {
  try {
    const snap = await getDocs(collection(db, CURATED_SUPPLIERS_COLLECTION));
    const out: Supplier[] = [];
    snap.forEach((d) => {
      const parsed = parseSupplierDoc(d.data() as Record<string, unknown>, d.id);
      if (parsed) out.push(parsed);
    });
    return out;
  } catch (e) {
    console.warn('[curatedSupplierStore] fetchCuratedSuppliers failed:', e);
    return [];
  }
}

/** Load company-specific supplier entries. Returns [] on failure. */
export async function fetchCompanySuppliers(companyId: string | null | undefined): Promise<Supplier[]> {
  if (!companyId) return [];
  try {
    const snap = await getDocs(companySuppliersCol(companyId));
    const out: Supplier[] = [];
    snap.forEach((d) => {
      const parsed = parseSupplierDoc(d.data() as Record<string, unknown>, d.id);
      if (parsed) out.push(parsed);
    });
    return out;
  } catch (e) {
    console.warn('[curatedSupplierStore] fetchCompanySuppliers failed:', e);
    return [];
  }
}

export async function saveCuratedSupplier(
  supplier: Supplier,
  meta: { updatedByEmail?: string; source?: CuratedSupplierSource },
): Promise<void> {
  const payload: CuratedSupplierDoc = {
    ...supplier,
    active: true,
    updatedAt: serverTimestamp(),
    ...(meta.updatedByEmail ? { updatedByEmail: meta.updatedByEmail } : {}),
    ...(meta.source ? { source: meta.source } : {}),
  };
  await setDoc(doc(db, CURATED_SUPPLIERS_COLLECTION, supplier.id), payload);
}

export async function deactivateCuratedSupplier(supplierId: string, updatedByEmail?: string): Promise<void> {
  await setDoc(
    doc(db, CURATED_SUPPLIERS_COLLECTION, supplierId),
    { active: false, updatedAt: serverTimestamp(), ...(updatedByEmail ? { updatedByEmail } : {}) },
    { merge: true },
  );
}

export async function saveCompanySupplier(
  companyId: string,
  supplier: Supplier,
  meta: { updatedByEmail?: string },
): Promise<void> {
  const payload: CompanySupplierDoc = {
    ...supplier,
    active: true,
    updatedAt: serverTimestamp(),
    ...(meta.updatedByEmail ? { updatedByEmail: meta.updatedByEmail } : {}),
  };
  await setDoc(doc(db, `companies/${companyId}/suppliers`, supplier.id), payload);
}

export async function deleteCompanySupplier(companyId: string, supplierId: string): Promise<void> {
  await deleteDoc(doc(db, `companies/${companyId}/suppliers`, supplierId));
}

/** Batch upsert baseline suppliers into curated_suppliers (admin seed). */
export async function batchUpsertCuratedSuppliers(
  suppliers: readonly Supplier[],
  meta: { updatedByEmail?: string; source?: CuratedSupplierSource },
): Promise<number> {
  const BATCH_SIZE = 400;
  let written = 0;
  for (let i = 0; i < suppliers.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = suppliers.slice(i, i + BATCH_SIZE);
    for (const s of chunk) {
      const payload: CuratedSupplierDoc = {
        ...s,
        active: true,
        updatedAt: serverTimestamp(),
        ...(meta.updatedByEmail ? { updatedByEmail: meta.updatedByEmail } : {}),
        ...(meta.source ? { source: meta.source } : {}),
      };
      batch.set(doc(db, CURATED_SUPPLIERS_COLLECTION, s.id), payload);
      written += 1;
    }
    await batch.commit();
  }
  return written;
}
